/**
 * client/src/lib/use-ladder-downgrade-push.ts
 *
 * Client half of Task 1b Step 4 — the `security:ladder_downgraded`
 * WebSocket notice (AC-45; server: `server/logic/security-tier-ladder/
 * anomaly-downgrade-notice.ts`, broadcasts to every socket of the
 * downgraded user). Mirrors `use-invitation-push.ts` exactly: same
 * `useCurrentUser()` readiness gate, same `{ type:"auth", userId, sessionId }`
 * handshake shape, same `resetKey`.
 *
 * `message` is one of the closed strings in `server/logic/
 * security-tier-ladder/anomaly-downgrade-copy.ts` (copy-audited server-side,
 * AC-45/AC-46). It is rendered VERBATIM by the caller — no client-side
 * template, no interpolation, no added metadata. Nothing here or downstream
 * may author a downgrade sentence.
 *
 * REACH IS NOT GUARANTEED — a WebSocket notice reaches a LIVE session only.
 * The load-bearing path is the per-action gate that asks the holder to
 * verify (the session's `aal` itself, re-read after invalidation by the
 * listener that consumes this hook); this notice only removes the surprise
 * of arriving at that gate, it is never the gate itself.
 *
 * Honest labelling: "unusual activity" describes an ABSENCE of expected
 * possession/context continuity, as the server copy itself is worded and
 * audited (AC-46) — never an attacker claim, never a liveness/deepfake
 * claim (CLAUDE.md §Continuous verification, Friction Rule clause 2).
 */

import { useCurrentUser } from "@/lib/use-current-user";
import { useReconnectingWs, type UseReconnectingWsResult } from "@/lib/use-reconnecting-ws";

export interface LadderDowngradeFrame extends Record<string, unknown> {
  type: "security:ladder_downgraded";
  level: number;
  message: string;
  timestamp: string;
}

/**
 * Listens for `security:ladder_downgraded` pushes addressed to the current
 * authenticated user. A null/loading current user opens no socket at all
 * (readiness-gate contract, same as `useInvitationPush`).
 */
export function useLadderDowngradePush(
  onEvent: (frame: LadderDowngradeFrame) => void,
): UseReconnectingWsResult {
  const { user } = useCurrentUser();

  return useReconnectingWs<LadderDowngradeFrame>(
    () =>
      user?.id
        ? { type: "auth", userId: user.id, sessionId: `ladder-downgrade-push-${user.id}` }
        : null,
    (msg) => msg.type === "security:ladder_downgraded" && typeof msg.message === "string",
    onEvent,
    user?.id,
  );
}
